interface IGetCookieOptions {
    encodeKey?: boolean;
    decodeValue?: boolean;
}

export class CookieJar<T extends Record<string, string> = Record<string, string>> {
    private _cookieObj: T;

    public constructor(cookieStr: T | string) {
        // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
        switch (typeof cookieStr) {
            case 'object':
                this._cookieObj = cookieStr;
                break;
            case 'string':
                this._cookieObj = parseCookie(cookieStr);
                break;
            default:
                throw TypeError('Unsupported type cookie.');
        }
    }

    public getCookie(key: string, options?: IGetCookieOptions): string | null {
        if (options) {
            let readKey = key;
            if (options.encodeKey) {
                readKey = encodeURIComponent(key);
            }
            let value = this._cookieObj[readKey];
            if (options.decodeValue) {
                value = decodeURIComponent(value);
            }
            return value;
        }
        return this._cookieObj[key] ?? null;
    }

    public setCookie<TKey extends keyof T>(
        key: TKey,
        value: T[TKey],
        expires?: Date,
        path?: string,
        domain?: string,
        secure?: boolean,
    ): void {
        setCookie(key as string, value, expires, path, domain, secure);
        this._cookieObj[key] = value;
    }

    public removeCookie<TKey extends keyof T>(key: TKey, path?: string, domain?: string, secure?: boolean): void {
        delete this._cookieObj[key];
        removeCookie(key as string, path, domain, secure);
    }
}

export function parseCookie<T extends Record<string, string>>(cookieStr: string): T {
    const rt: Record<string, string> = {};
    for (const item of cookieStr.trim().split(';')) {
        const trimedItem = item.trimStart();
        if (trimedItem) {
            let keyIndexEnd = trimedItem.indexOf('=');
            let valueIndexStart = keyIndexEnd + 1;
            const trimedItemLen = trimedItem.length;

            if (keyIndexEnd === -1) {
                keyIndexEnd = trimedItemLen;
                valueIndexStart = keyIndexEnd;
            }

            const key = trimedItem.substring(0, keyIndexEnd);
            const value = trimedItem.substring(valueIndexStart, trimedItemLen);

            rt[key] = value;
        }
    }
    return rt as T;
}

let globalCookieJar: CookieJar;

export const getGlobalCookieJar = (): CookieJar => {
    if (!globalCookieJar) {
        globalCookieJar = new CookieJar(document.cookie);
    }
    return globalCookieJar;
};

export function setCookie(
    name: string,
    value: string,
    /**
     * @default null
     */
    expires: Date | null = null,
    /**
     * @default '/'
     */
    path = '/',
    /**
     * @default location.hostname
     */
    domain: string = location.hostname,
    /**
     * https
     *
     * @default true
     */
    secure = true,
): void {
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)};expires=${
        expires ? expires.toUTCString() : 'null'
    };path=${path};domain=${domain}${!secure ? '' : ';secure'}`;
}

export function removeCookie(key: string, path?: string, domain?: string, secure?: boolean): void {
    setCookie(key, '', new Date(0), path, domain, secure);
}
